import React from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Container, Row } from 'reactstrap'
import {
  BootstrapTextInput,
  FormCard,
  SaveButton,
  TypeaheadMultiInput,
  UserProfileImageInput,
} from '../../components/forms'
import { useUser } from '../../utils/contexts/userContext'
import { _USER } from '../../utils/empties'
import {
  useAllQuery,
  useSingleQuery,
} from '../../utils/hooks/reactQuery/queries'
import { useItemMutation } from '../../utils/hooks/reactQuery/mutations'

const UserSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  username: Yup.string()
    .email('Must be a valid email')
    .required('Username is required'),
  password: Yup.string(),
  roles: Yup.array().of(Yup.string()),
  userGroups: Yup.array().of(Yup.string()),
  profileImage: Yup.string(),
})

function User() {
  const { id } = useParams()
  const { data = _USER, isLoading, isError, error } = useSingleQuery('user', id)
  const { update, add } = useItemMutation('user')
  const { user: loggedUser } = useUser()
  const { data: roles } = useAllQuery('roles')
  const { data: userGroups } = useAllQuery('user-groups')

  const handleSubmit = (values, { setSubmitting }) => {
    const toastId = toast.loading('Saving user...')

    // Are we updating or adding?
    if (id !== 'new') {
      update.mutate(values, {
        onSuccess: () => {
          console.log('Successfully Updated!')
          toast.update(toastId, {
            render: 'Saved user!',
            type: 'success',
            isLoading: false,
            autoClose: 1500,
          })
          setSubmitting(false)
        },
        onError: (error) => {
          console.error(error)
          toast.update(toastId, {
            render: 'Could not save user...',
            type: 'error',
            isLoading: false,
            autoClose: 1500,
          })
          setSubmitting(false)
        },
      })
    } else {
      add.mutate(values, {
        onSuccess: () => {
          console.log('Successfully Created!')
          toast.update(toastId, {
            render: 'Saved user!',
            type: 'success',
            isLoading: false,
            autoClose: 1500,
          })
        },
        onError: (error) => {
          console.error(error)
          toast.update(toastId, {
            render: 'Could not save user...',
            type: 'error',
            isLoading: false,
            autoClose: 1500,
          })
          setSubmitting(false)
        },
      })
    }
  }

  return (
    <Container fluid>
      {isLoading || !loggedUser ? (
        <h5>Loading...</h5>
      ) : isError ? (
        <h5>Error: {error.message}</h5>
      ) : (
        <FormCard
          breadcrumbLink="/dashboard/users"
          breadcrumbLabel="Users"
          breadcrumbItem={data.username.length > 0 ? data.username : 'New User'}
        >
          <Formik
            initialValues={data}
            validationSchema={UserSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ isValid, isSubmitting, values }) => (
              <Form>
                <UserProfileImageInput user={values} />
                <Row>
                  <BootstrapTextInput
                    label="First Name"
                    id="firstName"
                    name="firstName"
                    placeholder="First Name"
                  />
                  <BootstrapTextInput
                    label="Last Name"
                    id="lastName"
                    name="lastName"
                    placeholder="Last Name"
                  />
                  <BootstrapTextInput
                    label="Username"
                    id="username"
                    name="username"
                    placeholder="Username"
                  />
                  <BootstrapTextInput
                    label={id === 'new' ? 'Set Password' : 'Update Password'}
                    id="password"
                    name="password"
                    placeholder="Password"
                    isPassword
                    hide={!(id === 'new' || id === loggedUser._id)}
                  />
                  <TypeaheadMultiInput
                    label="Roles"
                    id="roles"
                    name="roles"
                    placeholder="Roles"
                    options={roles}
                    objects
                    labelKey="name"
                    valueKey="_id"
                  />
                  <TypeaheadMultiInput
                    label="User Groups"
                    id="userGroups"
                    name="userGroups"
                    placeholder="User Groups"
                    options={userGroups}
                    objects
                    labelKey="name"
                    valueKey="_id"
                  />
                </Row>
                <SaveButton disabled={isSubmitting || !isValid} />
              </Form>
            )}
          </Formik>
        </FormCard>
      )}
    </Container>
  )
}

export default User
